/**
 * Customer email (MAP 249, spec §1–§2): the outbox every email a shop sends to someone
 * outside the shop goes through, and the words for what became of one.
 *
 * ⚠️ Each list here has a SECOND COPY in SQL (migration 0045's CHECKs; SQL cannot import it).
 * `packages/db/test/email-migration.test.ts` writes every value of every list through them, so
 * a value added here without the migration fails there first.
 */
import { canSpamLines } from './campaigns';
import { SEND_OUTCOMES } from './delivery';

/**
 * What a customer email is (spec §6's catalog). Seven customer kinds, plus `team_invite`, which
 * goes on ServWell's own mail but still counts toward the shop's limit (§3).
 */
export const CUSTOMER_EMAIL_KINDS = [
  'invoice',
  'quote',
  'payment_reminder',
  'quote_follow_up',
  'quote_approved',
  'review_request',
  'service_reminder',
  'team_invite',
  'purchase_order',
] as const;
export type CustomerEmailKind = (typeof CUSTOMER_EMAIL_KINDS)[number];

/** The kinds a shop may reword (spec §6, ruling D: all seven customer kinds). */
export const EDITABLE_EMAIL_KINDS = CUSTOMER_EMAIL_KINDS.filter(
  (k): k is Exclude<CustomerEmailKind, 'team_invite' | 'purchase_order'> =>
    k !== 'team_invite' && k !== 'purchase_order',
);

/** At most one email of these per record, ever (the partial unique index, spec §1). */
export const ONCE_PER_RECORD_KINDS = [
  'quote_approved',
  'review_request',
  'service_reminder',
] as const satisfies readonly CustomerEmailKind[];

/**
 * The AUTOMATIC kinds: sent by a sweep, not by a person's click, so they go only inside the
 * shop's send window (spec §2 step 4, ruling B). `quote_approved` is automatic but not windowed:
 * it confirms what the customer just did.
 */
export const WINDOWED_EMAIL_KINDS = [
  'payment_reminder',
  'quote_follow_up',
  'review_request',
  'service_reminder',
] as const satisfies readonly CustomerEmailKind[];

/** What the email is about (`customer_emails.ref_type`), by kind. */
export const CUSTOMER_EMAIL_REF_TYPES = [
  'invoice',
  'quote',
  'job',
  'invitation',
  'purchase_order',
] as const;
export type CustomerEmailRefType = (typeof CUSTOMER_EMAIL_REF_TYPES)[number];

export const REF_TYPE_OF: Record<CustomerEmailKind, CustomerEmailRefType> = {
  invoice: 'invoice',
  payment_reminder: 'invoice',
  quote: 'quote',
  quote_follow_up: 'quote',
  quote_approved: 'quote',
  review_request: 'job',
  // The anchor job: the last completed one of the type (spec §6).
  service_reminder: 'job',
  team_invite: 'invitation',
  purchase_order: 'purchase_order',
};

/** Where an outbox row stands. A `queued` row may be WAITING (`waiting_for`). */
export const CUSTOMER_EMAIL_STATUSES = [
  'queued',
  'sending',
  'sent',
  'skipped',
  'failed',
] as const;
export type CustomerEmailStatus = (typeof CUSTOMER_EMAIL_STATUSES)[number];

/**
 * Why a `queued` row is not going yet (spec §1). Every one is re-checked: a wait with a time
 * (`daily_limit`, `send_window`, `route_limit`) at that time, the others every
 * `WAIT_RECHECK_MINUTES` and at once when the thing they wait for happens (a resume, a cleared
 * hold, a confirmed address).
 */
export const CUSTOMER_EMAIL_WAITS = [
  'daily_limit',
  'reply_address',
  'send_window',
  'shop_held',
  'shop_paused',
  'route_limit',
  'content_review',
  'owner_unconfirmed',
  /**
   * The shop's confirmed reply address, or its domain's From name, looks like a brand (MAP 249
   * Codex r2 #5): judged at every send by the current rules, as the shop's name is.
   */
  'sender_held',
] as const;
export type CustomerEmailWait = (typeof CUSTOMER_EMAIL_WAITS)[number];

/**
 * The waits a ticket email can have (`ticket_emails.waiting_for`). The gate runs for ticket
 * emails too (spec §2), and a ticket message is free text, so it can wait for a content review
 * and for the owner's confirmed address as well as the five spec §1 names. Never the send
 * window: a person wrote it.
 */
export const TICKET_EMAIL_WAITS = CUSTOMER_EMAIL_WAITS.filter(
  (w): w is Exclude<CustomerEmailWait, 'send_window'> => w !== 'send_window',
);
export type TicketEmailWait = (typeof TICKET_EMAIL_WAITS)[number];

/**
 * Why a `skipped` or `failed` row did not go: the shared delivery vocabulary, plus
 *  - `interrupted`: a worker died between the claim and the answer. It MAY have gone out, so it
 *    is never sent again (the ticket ledger's word);
 *  - `unconfirmed`: it MAY have gone and will not be tried again. A connected mailbox timed out
 *    or answered 5xx (it takes no idempotency key, spec §2 step 7); or an email that already had
 *    an unknown answer was refused later, outlived its key's window, or stopped being wanted;
 *  - `suppressed`: a promotional email to an address that asked not to hear from the shop;
 *  - `superseded`: something since made it wrong to send (a newer email covers it, or the
 *    invoice was paid or voided while it waited);
 *  - `internal`: ServWell itself kept failing on it (the gate threw `ROW_ERROR_BUDGET` times in a
 *    row) before any call, so it was not sent; staff see the error class in the logs.
 * A staff rejection of a held email is `skipped / rejected` (spec §4b): `rejected` on a
 * `failed` row is the mail service's refusal.
 */
export const CUSTOMER_EMAIL_FAILURES = [
  'no-recipient',
  'not-configured',
  'rejected',
  'throttled',
  'unreachable',
  'interrupted',
  'unconfirmed',
  'suppressed',
  'superseded',
  'internal',
] as const;
export type CustomerEmailFailure = (typeof CUSTOMER_EMAIL_FAILURES)[number];

/** "Sending from" (Settings → Email): the shop's chosen route. */
export const SENDING_MODES = ['servwell', 'domain', 'mailbox'] as const;
export type SendingMode = (typeof SENDING_MODES)[number];

/**
 * How an email actually left: the shop's three routes, or `platform` (ServWell's own mail,
 * `EMAIL_FROM`: invites, confirmations, the digest). Also the key of `email_day_counts`.
 */
export const EMAIL_ROUTES = ['servwell', 'domain', 'mailbox', 'platform'] as const;
export type EmailRoute = (typeof EMAIL_ROUTES)[number];

/** Resend's domain statuses (`shop_sending_domains.resend_status`, spec §7). */
export const SENDING_DOMAIN_STATUSES = [
  'not_started',
  'pending',
  'verified',
  'partially_verified',
  'partially_failed',
  'failed',
  'temporary_failure',
] as const;

export const MAILBOX_PROVIDERS = ['google', 'microsoft'] as const;
export const MAILBOX_ACCOUNT_KINDS = [
  'gmail',
  'workspace',
  'outlook_com',
  'm365',
] as const;
export type MailboxAccountKind = (typeof MAILBOX_ACCOUNT_KINDS)[number];
export const MAILBOX_STATUSES = ['connected', 'needs_reconnect', 'disconnected'] as const;

/**
 * `invoice_reminders.status` / `quote_reminders.status`: the send outcomes, plus `queued`
 * while the ledger's step waits in the outbox (spec §3).
 */
export const REMINDER_STATUSES = [...SEND_OUTCOMES, 'queued'] as const;

// ── Limits (spec §2 step 6, rulings 2026-10-09) ──────────────────────────────────────────

/** Every route, on the shop's first day (its first 24 hours). */
export const FIRST_DAY_LIMIT = 10;
/** The ServWell address, after the first day. */
export const SERVWELL_DAILY_LIMIT = 50;
/** The shop's own domain, after the first day. */
export const DOMAIN_DAILY_LIMIT = 150;
/** A connected mailbox: the provider's own safe ceiling (spec §8). */
export const MAILBOX_DAILY_CEILING: Record<MailboxAccountKind, number> = {
  gmail: 350,
  workspace: 1500,
  outlook_com: 300,
  m365: 2000,
};
/**
 * Every email that leaves through ServWell's one Resend account, all shops together, in a UTC day
 * (spec §11b): a circuit breaker. Past it, everything waits `route_limit` and staff get a notice
 * (ticket 04). Counted in `email_platform_counts`: one row per route, the ceiling over their SUM
 * (the ServWell address, a shop's domain and ServWell's own mail; a mailbox is not ours). Its
 * confirmation emails are NOT in it: they spend `CONFIRMATION_DAILY_ALLOWANCE` (D1).
 */
export const PLATFORM_DAILY_CEILING = 2000;

/**
 * ServWell's OWN confirmation emails in a UTC day, all shops and sign-ups together (MAP 249
 * abuse item D1): the sign-up confirmation (`/v/`) and the reply-address confirmation (`/c/`).
 * An allowance of their own, OUTSIDE the ceiling above: they never spend (or count toward) the
 * shops' `PLATFORM_DAILY_CEILING`, so a flood of throwaway sign-ups cannot make every shop's
 * invoices wait until midnight UTC, and it is capped low because such a flood bounces. Past it a
 * confirmation is not sent, and "Send it again" is refused in words until the next UTC day.
 * Counted in `email_platform_counts` on its own route, `confirmation`.
 */
export const CONFIRMATION_DAILY_ALLOWANCE = 300;

/**
 * The rows of `email_platform_counts`: every route through ServWell's Resend account (the
 * ceiling's, summed together) and `confirmation`, the confirmation allowance's (summed alone).
 */
export const PLATFORM_COUNT_ROUTES = [...EMAIL_ROUTES, 'confirmation'] as const;
export type PlatformCountRoute = (typeof PLATFORM_COUNT_ROUTES)[number];

/**
 * Hours a "possibly sent" email (an UNKNOWN answer) may still be retried, counted from the first
 * call with its key: Resend keeps an idempotency key 24 hours, and after that a retry with the same
 * key is a NEW email. One hour of margin.
 */
export const IDEMPOTENCY_WINDOW_HOURS = 23;

/** The highest a staff override may set (`shop_email_settings.daily_limit_override`). */
export const MAX_DAILY_LIMIT_OVERRIDE = 10_000;

/** Automatic emails go between these shop-local hours (spec §2 step 4, ruling B). */
export const SEND_WINDOW_START_HOUR = 8;
export const SEND_WINDOW_END_HOUR = 19;

// ── The automatic emails' timing and rules (spec §6, ticket 06) ──────────────────────────

/** A review request goes at this shop-local hour, the day after its job is closed (ruling B). */
export const REVIEW_REQUEST_HOUR = 10;
/**
 * At most one review request per customer in this many days: another one queued, sending, or
 * sent within them stops a new one (spec §6, Codex r1 #8).
 */
export const REVIEW_REQUEST_EVERY_DAYS = 90;
/** The service reminder sweep runs at this shop-local hour, once a day (spec §6). */
export const SERVICE_REMINDER_HOUR = 9;
/** "Remind after N months" on a service type: from 1 to 36, or never (the column's CHECK). */
export const REMIND_AFTER_MONTHS_MIN = 1;
export const REMIND_AFTER_MONTHS_MAX = 36;

// ── The From name (spec §2, R2 rec 1) ────────────────────────────────────────────────────

/** The From name when nothing of the shop's name survives `asciiName`. */
export const FALLBACK_SENDER_NAME = 'Your service team';
/** The longest a From name may be, cut on a word boundary. */
export const MAX_SENDER_NAME = 40;

/**
 * A shop's name as a From display name that NEVER needs RFC 5322 quoting:
 *  1. a curly apostrophe (a phone's `’`) becomes the shop's own `'`;
 *  2. NFKD, then the combining marks go (`José` → `Jose`, `Ｊｏｅ` → `Joe`);
 *  3. only `[A-Za-z0-9 &'-]` is kept, everything else becomes a space;
 *  4. spaces collapse, and it is cut to `MAX_SENDER_NAME` on a word boundary;
 *  5. nothing left → `FALLBACK_SENDER_NAME`.
 *
 * Every character left is RFC 5322 `atext` or the space between atoms, so the name is a phrase
 * as it stands: no quotes, no encoded words, nothing Resend refuses (`"`, `?`, `.` are gone).
 * The full Unicode name stays in the body.
 */
export function asciiName(name: string): string {
  const kept = name
    .replace(/[‘’ʼ]/g, "'")
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^A-Za-z0-9 &'-]/g, ' ')
    .replace(/ +/g, ' ')
    .trim();
  return capOnWord(kept, MAX_SENDER_NAME) || FALLBACK_SENDER_NAME;
}

/** At most `max` characters, cut at the last space that fits (or hard, for one long word). */
function capOnWord(s: string, max: number): string {
  if (s.length <= max) return s;
  const space = s.slice(0, max + 1).lastIndexOf(' ');
  return (space > 0 ? s.slice(0, space) : s.slice(0, max)).trim();
}

/** The longest a subject may be once it is one line. */
export const MAX_SUBJECT = 250;

/**
 * A subject as ONE header line: every run of whitespace or control characters (CR and LF, the
 * Unicode line and paragraph separators included) becomes one space, then it is trimmed and
 * capped at `MAX_SUBJECT`. Every customer email's subject goes through this (spec §2).
 */
export function oneLine(s: string): string {
  return (
    s
      // eslint-disable-next-line no-control-regex
      .replace(/[\s\u0000-\u001f\u007f-\u009f\u2028\u2029]+/g, ' ')
      .trim()
  );
}

/** `oneLine`, capped at `MAX_SUBJECT` (by code point, so no surrogate half is left). */
export function emailSubject(s: string): string {
  const line = Array.from(oneLine(s));
  return line.length <= MAX_SUBJECT ? line.join('') : line.slice(0, MAX_SUBJECT).join('');
}

// ── The footer (spec §2) ─────────────────────────────────────────────────────────────────

/** Where a customer reports a shop's email. An alias at ServWell (go-live checklist). */
export const ABUSE_ADDRESS = 'abuse@servwell.io';

/**
 * The reference a customer quotes in an abuse report: the LAST 8 hex digits of the email's id.
 * Not the first 8 (spec §2's words): a uuidv7 starts with its timestamp, so every email in the
 * same ~65 seconds would share them. The tail is random. Staff find it with
 * `id::text like '%' || ref`.
 */
export function emailRef(id: string): string {
  return id.replace(/-/g, '').slice(-8);
}

/**
 * What ServWell adds under a shop's email (spec §2):
 *  - on the ServWell address and the shop's own domain (`servwell`, the default): who sent it and
 *    where to report it, and, when there is no Reply-To, that a reply will not reach the shop;
 *  - on a PROMOTIONAL email (a review request, a service reminder; ticket 06), on every route the
 *    mailbox's included: the law's three lines (`canSpamLines`: why you got this, the shop's
 *    postal address, the unsubscribe link).
 * `shop` is the shop's full name (one line). Empty when there is nothing to add (a mailbox's
 * transactional email).
 */
export function customerEmailFooter(input: {
  shop: string;
  id: string;
  hasReplyTo: boolean;
  /** ServWell's own lines (the ServWell address and the shop's domain). Default true. */
  servwell?: boolean;
  /** A promotional email's unsubscribe block: the shop's postal address and the link. */
  unsubscribe?: { postalAddress: string; url: string } | null;
}): string {
  const shop = oneLine(input.shop) || FALLBACK_SENDER_NAME;
  // "… Ltd." ends the sentence itself: no second full stop.
  const stop = /[.!?]$/.test(shop) ? '' : '.';
  const lines = [
    ...(input.servwell === false
      ? []
      : [
          ...(input.hasReplyTo
            ? []
            : [`Replies to this email don't reach ${shop}${stop}`]),
          `Sent by ${shop} using ServWell. Report abuse: ${ABUSE_ADDRESS} (ref ${emailRef(input.id)})`,
        ]),
    ...(input.unsubscribe
      ? canSpamLines({
          shopName: shop,
          postalAddress: input.unsubscribe.postalAddress,
          unsubscribeUrl: input.unsubscribe.url,
        })
      : []),
  ];
  return lines.length > 0 ? ['', '—', ...lines].join('\n') : '';
}

/**
 * The headers a promotional email carries (spec §2, RFC 2369 + RFC 8058): its unsubscribe link,
 * and that one POST to it is enough (mail programs show their own "Unsubscribe" button).
 */
export function unsubscribeHeaders(url: string): Record<string, string> {
  return {
    'List-Unsubscribe': `<${url}>`,
    'List-Unsubscribe-Post': 'List-Unsubscribe=One-Click',
  };
}

// ── What a person reads about one ────────────────────────────────────────────────────────

/** "Waiting: …" for each wait, on a detail page (spec §3). */
export const CUSTOMER_EMAIL_WAIT_LINE: Record<CustomerEmailWait, string> = {
  // No time and no day (Codex 07-r1 #2, r2 #1, r3 #3): a wait set yesterday reads after midnight
  // too, a try may meet the limit again, and staff raising the limit releases it at once. The
  // row's own schedule is the only true time, and this line does not carry it.
  daily_limit: 'Waiting for a daily email limit',
  reply_address: 'Waiting for your reply address to be confirmed',
  send_window: 'Waiting: automatic emails go between 8 AM and 7 PM',
  shop_held: 'Waiting: ServWell is reviewing your shop’s emails',
  shop_paused: 'Waiting: ServWell has paused your shop’s emails',
  // A connected mailbox's ceiling (spec §8), or ServWell's own daily ceiling (§11b).
  route_limit: 'Waiting: a sending limit outside your shop is used up for now',
  content_review: 'Waiting: ServWell is checking this email before it goes',
  owner_unconfirmed: 'Waiting for the owner to confirm their email address',
  sender_held: 'Waiting: your reply address or From name looks like another company’s',
};

/**
 * What happened, for the failures the shared delivery vocabulary has no word for (the others
 * read `deliveryProblem`, one vocabulary, `delivery.ts`). Same contract: sentence case, no
 * trailing full stop, a fact and not an instruction. `interrupted` and `unconfirmed` MAY have
 * gone out, so their screens never say "didn't send".
 */
export const CUSTOMER_EMAIL_FAILURE_LINE: Record<
  'interrupted' | 'unconfirmed' | 'suppressed' | 'superseded' | 'internal',
  string
> = {
  interrupted: 'The send was cut off before the mail service confirmed it',
  unconfirmed: 'We couldn’t confirm this went',
  suppressed: 'This customer asked not to get these emails',
  superseded: 'Something changed after it was queued, so it was not needed',
  internal: 'ServWell kept hitting a problem preparing it',
};

/** The next move for each of those, addressed to the shop (`deliveryFix`'s voice), or none. */
export const CUSTOMER_EMAIL_FAILURE_FIX: Record<
  keyof typeof CUSTOMER_EMAIL_FAILURE_LINE,
  string | null
> = {
  interrupted: 'Check with them before sending it again.',
  // Any route now (a retry window that closed, a later refusal after an unknown answer), so not
  // only a mailbox's Sent folder.
  unconfirmed:
    'Check with them, or your Sent folder if you send from your own mailbox, before sending it again.',
  suppressed: null,
  superseded: null,
  internal: 'Send it again, or contact ServWell if it keeps happening.',
};
